import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Tag, ControlLink, Breakpoint, Stack, useBreakpoint } from '@moda/om';
import { PageFilterFragment, PageFilterFiltersFragment } from '../../generated/types';
import { usePLPParams } from '../../hooks/usePLPParams';
import { useCategoryTree } from '../../hooks/useCategoryTree';
import { ActivePageFilter, useActivePageFilters } from './useActivePageFilters';

import './PageFilterClear.scss';

interface Props {
  attributes: PageFilterFiltersFragment['attributes'];
  designers: PageFilterFiltersFragment['designers'];
  sizes: PageFilterFiltersFragment['sizes'];
  categoryTree: PageFilterFragment['categoryTree'] | null;
  showCategoryFilters?: boolean;
}

export const PageFilterClear: React.FC<Props> = ({
  attributes,
  designers,
  sizes,
  categoryTree,
  showCategoryFilters = false
}) => {
  const history = useHistory();
  const { removeFilter, clearFilter, clearAllFilters } = usePLPParams();

  const { topLevelNode, getCategoryNodeHref } = useCategoryTree(
    categoryTree,
    (attributes?.length || 0) > 0
  );

  const isMobile = useBreakpoint({ lt: 'sm' }).matches;
  const includeCategoryChips = isMobile || showCategoryFilters;

  const filters = useActivePageFilters({
    attributes,
    designers,
    sizes,
    categoryTree,
    includeCategories: includeCategoryChips
  });

  const removeFilterOrCategory = useCallback(
    (filter: ActivePageFilter) => {
      if (filter.key === 'vertical' || filter.key === 'category' || filter.key === 'subcategory')
        return history.push(filter.href);

      if (filter.key === 'sale') return clearFilter(['sale', 'saletags']);

      return removeFilter(filter.key, filter.value);
    },
    [history, removeFilter, clearFilter]
  );

  const handleClearAll = useCallback(() => {
    const topLevelCategoryHref =
      topLevelNode && getCategoryNodeHref(topLevelNode, { removeFilters: true });

    return includeCategoryChips && topLevelCategoryHref
      ? history.push(topLevelCategoryHref)
      : clearAllFilters();
  }, [topLevelNode, getCategoryNodeHref, includeCategoryChips, history, clearAllFilters]);

  if (filters.length === 0) return null;

  const tags = filters.map(filter => (
    <Tag
      key={filter.value}
      className="PageFilterClear__tag"
      onClick={() => isMobile && removeFilterOrCategory(filter)}
      onRemove={() => removeFilterOrCategory(filter)}
      style={{ textTransform: 'capitalize' }}
    >
      {filter.label}
    </Tag>
  ));

  const clearAll = filters.length > 1 && (
    <ControlLink className="PageFilterClear__clear-all" onClick={handleClearAll}>
      Clear All
    </ControlLink>
  );

  const pillsMarkup = (
    <Stack space={3} alignItems="flex-start">
      <Stack className="PageFilterClear__filters" direction="horizontal" space={3}>
        {tags}
      </Stack>
      {clearAll}
    </Stack>
  );

  return (
    <div className="PageFilterClear">
      {showCategoryFilters ? (
        <>
          {tags}
          {clearAll}
        </>
      ) : (
        <>
          <Breakpoint lt="md">{pillsMarkup}</Breakpoint>
          <Breakpoint gt="md">
            {tags}
            {clearAll}
          </Breakpoint>
        </>
      )}
    </div>
  );
};
